import classNames from 'classnames';
import { ContextService } from '../../services/context_service';
import { i18n } from '../../i18n/i18n';
import { lazyInject } from '../../services/inversify';
import * as React from 'react';
import style from './datenauswertung.less'
import styleWForm from '../../core/forms/style/WForm.less'

interface DatenauswertungJahrauswahlProps extends React.ClassAttributes<any> {
    jahrMin: number;
    jahrMax: number;
    jahrFirst: number;
    jahrLast: number;
    contextKey?: string;
    externalHeaderElements?: Array<JSX.Element>;
    onFirstJahrChange: (jahr: number) => void;
    onLastJahrChange: (jahr: number) => void;
    renderClean?: boolean;
    wrapClass?: any;
}

interface DatenauswertungJahrauswahlState {
    dummy?: number;
    von: number;
    bis: number;
}

export class DatenauswertungJahrauswahl extends React.Component<DatenauswertungJahrauswahlProps, DatenauswertungJahrauswahlState> {

    constructor(props: any) {
        super(props);
        this.state = {
            von: this.props.jahrFirst,
            bis: this.props.jahrLast
        };
    }

    @lazyInject(ContextService) private contextService: ContextService;

    private timeoutIdVon: number;
    private timeoutIdBis: number;
    private timeout = 1000;

    componentDidMount(): void {
        if (this.props.contextKey) {
            const von = this.contextService.get(this.props.contextKey + '_von');
            const bis = this.contextService.get(this.props.contextKey + '_bis');
            if (bis) {
                this.props.onLastJahrChange(bis);
            }
            if (von) {
                this.props.onFirstJahrChange(von);
            }
        }
    }

    componentDidUpdate(prevProps: any, prevState: any): void {
        if (prevProps.jahrFirst !== this.props.jahrFirst) {
            this.setState({ von: this.props.jahrFirst });
        }
        if (prevProps.jahrLast !== this.props.jahrLast) {
            this.setState({ bis: this.props.jahrLast });
        }
    }

    onChange(event: any, context: string): void {
        const val = event.target.value;
        // Erlaube leere Eingabe (für Backspace) oder wandle in Zahl um
        const jahr = val === '' ? '' : +val;
        const otherJahr = context === 'von'
            ? +this.state.bis
            : +this.state.von;
        const newState = context === 'von'
            ? {von: +jahr, bis: otherJahr}
            : {von: otherJahr, bis: +jahr};

        // 1. Update immer den lokalen State, damit das Tippen flüssig bleibt
        this.setState(newState, () => {
            // 2. Validierung für die Weitergabe nach außen (Props)
            const isVierstellig = ('' + jahr).length === 4;
            const isInRange = (+jahr >= this.props.jahrMin && +jahr <= this.props.jahrMax);

            if (isVierstellig && isInRange) {
                 if (context === 'von') {
                    this.props.onFirstJahrChange(+jahr);
                } else {
                    this.props.onLastJahrChange(+jahr);
                }

                if (this.props.contextKey) {
                    this.contextService.set(`${this.props.contextKey}_${context}`, +jahr);
                }
            }
        });
    }

    changeYear(delta: number, context: string): void {
        const var_context = context === 'von' ? this.state.von : this.state.bis;
        const currentYear = +(var_context) || new Date().getFullYear();
        const newYear = currentYear + delta;

        const otherCurrentYear = context === 'von' ? +this.state.bis : this.state.von;

        if ((newYear >= +this.props.jahrMin && newYear <= +this.props.jahrMax)) {
            const newState = context === 'von'
                ? { von: newYear, bis: otherCurrentYear }
                : { bis: newYear, von: otherCurrentYear };

            this.setState(newState, () => {
                if (context === 'von') {
                    this.props.onFirstJahrChange(newYear);
                } else {
                    this.props.onLastJahrChange(newYear);
                }

                if (this.props.contextKey) {
                    this.contextService.set(`${this.props.contextKey}_${context}`, newYear);
                }
            });
        }
    }

    checkBis(): void {
        let bis = this.state.bis;
        let changeBis = false;

        const timeoutId = window.setTimeout(() => {
            if (bis < this.props.jahrMin) {
                bis = this.props.jahrMin;
                changeBis = true;
            }
            if (bis > this.props.jahrMax) {
                bis = this.props.jahrMax;
                changeBis = true;
            }
            if (bis < this.props.jahrFirst) {
                bis = this.props.jahrFirst;
                changeBis = true;
            }
            if (changeBis) {
                this.setState({ bis }, () => {
                    this.props.onLastJahrChange(bis);
                    if (this.props.contextKey) {
                        this.contextService.set(`${this.props.contextKey}_bis`, bis);
                    }
                });
            }
        }, this.timeout);
        clearTimeout(this.timeoutIdBis);
        this.timeoutIdBis = timeoutId;
    }

    checkVon(): void {
        let von = this.state.von;
        let changeVon = false;
        const timeoutId = window.setTimeout(() => {
            if (von < this.props.jahrMin) {
                von = this.props.jahrMin;
                changeVon = true;
            }
            if (von > this.props.jahrMax) {
                von = this.props.jahrMax;
                changeVon = true;
            }
            if (von > this.props.jahrLast) {
                von = this.props.jahrLast;
                changeVon = true;
            }
            if (changeVon) {
                this.setState({ von }, () => {
                    this.props.onFirstJahrChange(von);
                    if (this.props.contextKey) {
                        this.contextService.set(this.props.contextKey + '_von', von);
                    }
                });
            }
        }, this.timeout);
        clearTimeout(this.timeoutIdVon);
        this.timeoutIdVon = timeoutId;
    }

    renderCleanElements(): JSX.Element | JSX.Element[] {
        const currentJahr_von = +this.state.von || +this.props.jahrFirst || +this.props.jahrMin;
        const hideUp_von = currentJahr_von >= +this.props.jahrMax;
        const hideDown_von = currentJahr_von <= +this.props.jahrMin;

        const currentJahr_bis = +this.state.bis || +this.props.jahrLast || +this.props.jahrMax;
        const hideUp_bis = currentJahr_bis >= +this.props.jahrMax;
        const hideDown_bis = currentJahr_bis <= +this.props.jahrMin;

        const von = <>
            <label>{i18n('Jahr von', 'sys')}</label>
            <div className={style.datenauswertungNumberInputContainer}>
                <input
                    className={classNames(style.datenauswertungFormFieldInput, {
                        [style.hideUp]: hideUp_von,
                        [style.hideDown]: hideDown_von
                    })}
                    type='number'
                    value={this.state.von}
                    onChange={(event: any) => this.onChange(event, 'von')}
                    onBlur = {() => {
                        let jahr = +this.state.von;
                        if (jahr < this.props.jahrMin) jahr = this.props.jahrMin;
                        if (jahr > this.props.jahrMax) jahr = this.props.jahrMax;
                        this.setState({ von: jahr }, () => {
                            this.props.onFirstJahrChange(jahr);
                            if (this.props.contextKey) {
                                this.contextService.set(`${this.props.contextKey}_von`, jahr);
                            }
                        });
                    }}
                /> 
                <div
                    className={classNames(style.datenauswertungArrowTrigger, style.datenauswertungArrowUp, {
                        [style.isHidden]: hideUp_von
                    })}
                    onClick={() => this.changeYear(1, 'von')}
                    title={i18n('Mehr', 'sys')}
                />
                <div
                    className={classNames(style.datenauswertungArrowTrigger, style.datenauswertungArrowDown, {
                        [style.isHidden]: hideDown_von
                    })}
                    onClick={() => this.changeYear(-1, 'von')}
                    title={i18n('Weniger', 'sys')}
                />
            </div>
        </>;
        const bis = <>
            <label>{i18n('bis', 'sys')}</label>
            <div className={style.datenauswertungNumberInputContainer}>
                <input
                    className={classNames(style.datenauswertungFormFieldInput, {
                        [style.hideUp]: hideUp_bis,
                        [style.hideDown]: hideDown_bis
                    })}
                    type='number'
                    value={this.state.bis}
                    onChange={(event: any) => this.onChange(event, 'bis')}
                    onBlur = {() => {
                        let jahr = +this.state.bis;
                        if (jahr < this.props.jahrMin) jahr = this.props.jahrMin;
                        if (jahr > this.props.jahrMax) jahr = this.props.jahrMax;
                        
                        this.setState({ bis: jahr }, () => {
                            this.props.onLastJahrChange(jahr);
                            if (this.props.contextKey) {
                                this.contextService.set(`${this.props.contextKey}_bis`, jahr);
                            }
                        });
                    }}
                />
                <div
                    className={classNames(style.datenauswertungArrowTrigger, style.datenauswertungArrowUp, {
                        [style.isHidden]: hideUp_bis
                    })}
                    onClick={() => this.changeYear(1, 'bis')}
                    title={i18n('Mehr', 'sys')}
                />
                <div
                    className={classNames(style.datenauswertungArrowTrigger, style.datenauswertungArrowDown, {
                        [style.isHidden]: hideDown_bis
                    })}
                    onClick={() => this.changeYear(-1, 'bis')}
                    title={i18n('Weniger', 'sys')}
                />
            </div>
        </>;

        if (this.props.wrapClass) {
            return <>
                <div className={this.props.wrapClass}>{von}</div>
                <div className={this.props.wrapClass}>{bis}</div>
            </>;
        }
        return [von, bis];
    }

    render(): JSX.Element | JSX.Element[] {
        if (this.props.renderClean) {
            return this.renderCleanElements();
        }
        this.checkVon();
        this.checkBis();
        const currentJahr_bis: number = +this.state.bis || +this.props.jahrLast || +this.props.jahrMax;
        const currentJahr_von: number = +this.state.von || +this.props.jahrFirst || +this.props.jahrMin;
        const hideDown_bis: boolean = currentJahr_bis <= +this.props.jahrMin;
        const hideDown_von: boolean = currentJahr_von <= +this.props.jahrMin;
        const hideUp_bis: boolean = currentJahr_bis >= +this.props.jahrMax;
        const hideUp_von: boolean = currentJahr_von >= +this.props.jahrMax;

        return (
            <div style={{ flex: 1, flexDirection: 'row', display: 'flex', backgroundColor: '#eee' }}>
                <div style={{ padding: 5, margin: 'auto 0 auto 16px' }}>{i18n('Jahr', 'sys')}</div>
                <div className={styleWForm.inputNumberContainer} style={{ padding: 5, margin: 'auto 0' }}>
                    <input
                        type='number'
                        value={this.state.von}
                        onChange={(event: any) => this.onChange(event, 'von')}
                        onBlur = {() => {
                            let jahr = +this.state.von;
                            if (jahr < this.props.jahrMin) jahr = this.props.jahrMin;
                            if (jahr > this.props.jahrMax) jahr = this.props.jahrMax;
                            this.setState({ von: jahr }, () => {
                                this.props.onFirstJahrChange(jahr);
                                if (this.props.contextKey) {
                                    this.contextService.set(`${this.props.contextKey}_von`, jahr);
                                }
                            });
                        }}
                        // onChange={(evt: any) => {
                        //     const jahr = evt.target.value;
                        //     if (jahr >= this.props.jahrMin && jahr <= this.props.jahrMax && jahr <= this.props.jahrLast) {
                        //         this.props.onFirstJahrChange(jahr);
                        //         if (this.props.contextKey) {
                        //             this.contextService.set(this.props.contextKey + '_von', jahr);
                        //         }
                        //     }
                        // }}
                        // onInput={(evt: any) => {
                        //     let von = evt.target.value;
                        //     this.setState({ von }, () => {
                        //         const timeoutId = window.setTimeout(() => {
                        //             if (von < this.props.jahrMin) {
                        //                 von = this.props.jahrMin;
                        //             }
                        //             if (von > this.props.jahrMax) {
                        //                 von = this.props.jahrMax;
                        //             }
                        //             if (von > this.props.jahrLast) {
                        //                 von = this.props.jahrLast;
                        //             }
                        //             this.setState({ von }, () => {
                        //                 this.props.onFirstJahrChange(von);
                        //                 if (this.props.contextKey) {
                        //                     this.contextService.set(this.props.contextKey + '_von', von);
                        //                 }
                        //             });
                        //         }, this.timeout);
                        //         clearTimeout(this.timeoutIdVon);
                        //         this.timeoutIdVon = timeoutId;
                        //     });
                        // }}
                    />
                    <div
                        className={classNames(styleWForm.arrowUp, hideUp_von ? styleWForm.isHidden : null)}
                        onClick={() => this.changeYear(1, 'von')}
                        title={i18n('Mehr', 'sys')}
                    />
                    <div
                        className={classNames(styleWForm.arrowDown, hideDown_von ? styleWForm.isHidden : null)}
                        onClick={() => this.changeYear(-1, 'von')}
                        title={i18n('Weniger', 'sys')}
                    />
                </div>
                <div style={{ padding: 5, margin: 'auto 0' }}>{i18n('bis', 'sys')}</div>
                <div className={styleWForm.inputNumberContainer} style={{ padding: 5, margin: 'auto 0' }}>
                    <input
                        type='number'
                        value={this.state.bis}
                        onChange={(event: any) => this.onChange(event, 'bis')}
                        onBlur = {() => {
                            let jahr = +this.state.bis;
                            if (jahr < this.props.jahrMin) jahr = this.props.jahrMin;
                            if (jahr > this.props.jahrMax) jahr = this.props.jahrMax;
                            this.setState({ bis: jahr }, () => {
                                this.props.onLastJahrChange(jahr);
                                if (this.props.contextKey) {
                                    this.contextService.set(`${this.props.contextKey}_bis`, jahr);
                                }
                            });
                        }}
                        // onChange={(evt: any) => {
                        //     const jahr = evt.target.value;
                        //     if (jahr >= this.props.jahrMin && jahr <= this.props.jahrMax && jahr >= this.props.jahrFirst) {
                        //         this.props.onLastJahrChange(jahr);
                        //         if (this.props.contextKey) {
                        //             this.contextService.set(this.props.contextKey + '_bis', jahr);
                        //         }
                        //     }
                        // }}
                        // onInput={(evt: any) => {
                        //     let bis = evt.target.value;
                        //     this.setState({ bis }, () => {
                        //         const timeoutId = window.setTimeout(() => {
                        //             if (bis < this.props.jahrMin) {
                        //                 bis = this.props.jahrMin;
                        //             }
                        //             if (bis > this.props.jahrMax) {
                        //                 bis = this.props.jahrMax;
                        //             }
                        //             if (bis < this.props.jahrFirst) {
                        //                 bis = this.props.jahrFirst;
                        //             }
                        //             this.setState({ bis }, () => {
                        //                 this.props.onLastJahrChange(bis);
                        //                 if (this.props.contextKey) {
                        //                     this.contextService.set(this.props.contextKey + '_bis', bis);
                        //                 }
                        //             });
                        //         }, this.timeout);
                        //         clearTimeout(this.timeoutIdBis);
                        //         this.timeoutIdBis = timeoutId;
                        //     });
                        // }}
                    />
                    <div
                        className={classNames(styleWForm.arrowUp, hideUp_bis ? styleWForm.isHidden : null)}
                        onClick={() => this.changeYear(1, 'bis')}
                        title={i18n('Mehr', 'sys')}
                    />
                    <div
                        className={classNames(styleWForm.arrowDown, hideDown_bis ? styleWForm.isHidden : null)}
                        onClick={() => this.changeYear(-1, 'bis')}
                        title={i18n('Weniger', 'sys')}
                    />
                </div>
                {this.props.externalHeaderElements && this.props.externalHeaderElements.map((elem: JSX.Element, idx: number) =>
                    <div style={{ display: 'inline', marginLeft: 10 }} key={`elem-${idx}`}>{elem}</div>)
                }
            </div>
        );
    }
}
